/**
 * Organization Users Page
 *
 * Displays users belonging to a specific organization.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Shield } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Button,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Loader,
} from '@/components/common';
import { organizationService } from '@/api/services';
import type { OrganizationUser, OrganizationDetails } from '@/api/types';
import { formatDate } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';
import { EXTERNAL_URLS, IS_PRODUCTION } from '@/config/app.config';

// ============================================
// Page Component
// ============================================

export function OrganizationUsersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  // State
  const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch organization details
  useEffect(() => {
    if (!id) return;

    const fetchOrg = async () => {
      setIsLoadingOrg(true);
      try {
        const orgData = await organizationService.getOrganizationById(id);
        setOrganization(orgData);
      } catch (error) {
        console.error('Failed to fetch organization:', error);
        toast.error('Failed to load organization');
      } finally {
        setIsLoadingOrg(false);
      }
    };

    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch users
  const fetchUsers = useCallback(
    async (params: { page: number; size: number; search?: string }) => {
      if (!id) return;

      setIsLoadingUsers(true);
      try {
        const result = await organizationService.getOrganizationUsers(id, params);
        setUsers(result.userList);
        setTotalRows(result.filteredTotalRecords);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoadingUsers(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users when pagination or debounced search changes
  useEffect(() => {
    fetchUsers({ page: pagination.pageIndex, size: pagination.pageSize, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchTerm]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      // Reset to first page when searching
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // Handle OTP verification redirect
  const handleOTPVerification = useCallback(
    (user: OrganizationUser) => {
      navigate(`/support/${id}/${user.id}/otp`);
    },
    [id, navigate]
  );

  // Handle support session
  const handleSupportSession = useCallback(
    (user: OrganizationUser) => {
      // Open support session URL in new window
      const supportUrl = IS_PRODUCTION
        ? EXTERNAL_URLS.supportSession.production
        : EXTERNAL_URLS.supportSession.development;

      window.open(
        `${supportUrl}?orgId=${id}&userId=${user.id}`,
        '_blank',
        'noopener,noreferrer'
      );
    },
    [id]
  );

  // Column definitions
  const columns = useMemo<ColumnDef<OrganizationUser>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <User className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium">{row.original.username}</span>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span>
            {row.original.firstName || row.original.name}{' '}
            {row.original.lastName || ''}
          </span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Mail className="h-3 w-3" />
            {row.original.email}
          </div>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Phone className="h-3 w-3" />
            {row.original.mobile}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const variant =
            status === 'ACTIVE'
              ? 'success'
              : status === 'SUSPENDED'
                ? 'warning'
                : status === 'TERMINATED'
                  ? 'danger'
                  : 'default';
          return (
            <Badge variant={variant} dot>
              {status}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-foreground-muted dark:text-foreground-dark-muted">
            {formatDate(row.original.createdDate)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOTPVerification(row.original);
              }}
              leftIcon={<Shield className="h-3 w-3" />}
            >
              OTP
            </Button>
          </div>
        ),
      },
    ],
    [handleOTPVerification, handleSupportSession]
  );

  // Loading state for org
  if (isLoadingOrg) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader size="lg" message="Loading..." />
      </div>
    );
  }

  // Not found state
  if (!organization) {
    return (
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground dark:text-foreground-dark">
          Organization not found
        </h2>
        <Button className="mt-4" onClick={() => navigate('/organizations')}>
          Back to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/organizations/${id}`)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Organization Users
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            {organization.name} ({organization.code})
          </p>
        </div>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="Users"
          description={`${totalRows} user${totalRows !== 1 ? 's' : ''} in this organization`}
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={users}
            isLoading={isLoadingUsers}
            searchable
            searchPlaceholder="Search users..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage="No users found"
            emptyDescription="This organization has no users yet."
            className="border-0"
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default OrganizationUsersPage;
